import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { breadcrumbSchema } from "@/lib/schemas";
import { FIRM_EMAIL, FIRM_PHONE, MAIN_OFFICE_PHONE, OFFICES } from "@/lib/content";

/**
 * Shared LegalPage layout — used by /privacy and /terms.
 * Content is passed in as `sections` prop; SEO built from title.
 */
export function LegalPage({ title, effective, path, intro, sections, breadcrumbName }) {
  return (
    <div className="bg-ivory" data-testid={`legal-page-${path.replace("/", "")}`}>
      <SEO
        title={title}
        description={intro}
        path={path}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: breadcrumbName, path },
        ])}
      />

      <section className="pt-40 pb-16 max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="kicker">Legal</div>
        <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[0.98]">{title}</h1>
        <div className="mt-6 text-xs uppercase tracking-[0.28em] text-slate2/70">Effective: {effective}</div>
        <p className="mt-8 max-w-3xl text-lg text-slate2 font-light leading-relaxed">{intro}</p>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-12 pb-32 space-y-14">
        {sections.map((s, i) => (
          <article key={s.heading} className="hairline pt-10" data-testid={`legal-section-${i}`}>
            <div className="text-[10px] uppercase tracking-[0.32em] text-brass">{String(i + 1).padStart(2, "0")} · Section</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-navy leading-[1.1]">{s.heading}</h2>
            {s.body.map((p, j) => (
              <p key={j} className="mt-5 text-base md:text-lg text-slate2 font-light leading-relaxed max-w-3xl">{p}</p>
            ))}
          </article>
        ))}

        <div className="hairline pt-10">
          <div className="kicker">Contact the Firm</div>
          <p className="mt-4 text-slate2 font-light">
            Questions about this document? Write to&nbsp;
            <a href={FIRM_EMAIL.href} className="text-oxblood underline">{FIRM_EMAIL.display}</a>&nbsp;
            or call our principal office at&nbsp;
            <a href={MAIN_OFFICE_PHONE.href} className="text-oxblood underline">{MAIN_OFFICE_PHONE.display}</a>.
          </p>
          <p className="mt-3 text-sm text-slate2/70 font-light">
            {OFFICES[0].line1}, {OFFICES[0].line2}
          </p>
          <div className="mt-8 flex gap-4">
            <Link to="/privacy" className="btn-ghost">Privacy Policy</Link>
            <Link to="/terms" className="btn-ghost">Terms of Use</Link>
            <Link to="/legal" className="btn-ghost" data-testid="legal-link-disclosures">Legal Disclosures</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      breadcrumbName="Privacy Policy"
      path="/privacy"
      effective="February 1, 2026"
      intro="Lone Star Counsel respects your privacy. This policy explains what information we collect through LoneStarCounsel.com, how we use it, and the choices you have."
      sections={[
        {
          heading: "Information We Collect",
          body: [
            "We collect information you voluntarily provide through our intake form, contact form, and journal-subscription form — including your name, email address, phone number, mailing address, the subject matter of your inquiry, and any additional details you choose to share.",
            "We also automatically collect standard technical information (browser type, device type, IP address, referring URL, and pages visited) through cookies and server logs. This is used for security, analytics, and site performance.",
          ],
        },
        {
          heading: "How We Use Your Information",
          body: [
            "Information you submit is used to respond to your inquiry, evaluate potential engagement, deliver requested content (e.g., the Legacy Planning Guide), and — where you have subscribed — send occasional firm publications.",
            "We do not sell your information. We do not share it with third parties for marketing purposes.",
          ],
        },
        {
          heading: "Confidentiality Note",
          body: [
            "Submitting a form on this website does not create an attorney-client relationship, and communications you send through this site are not privileged until an engagement letter has been executed. Please do not send sensitive or confidential information through the website until we have confirmed a formal engagement.",
          ],
        },
        {
          heading: "Cookies and Analytics",
          body: [
            "We use cookies and standard analytics tools (such as Google Analytics) to measure site traffic and improve user experience. You may disable cookies through your browser settings; some site features may not function fully as a result.",
          ],
        },
        {
          heading: "Data Security",
          body: [
            "We use commercially reasonable technical and organizational measures to safeguard information submitted through the site. No online transmission is perfectly secure, and we cannot guarantee absolute security.",
          ],
        },
        {
          heading: "Your Rights",
          body: [
            "You may request access to, correction of, or deletion of your personal information at any time by writing to info@LoneStarCounsel.com. Residents of California and certain other jurisdictions may have additional statutory rights.",
          ],
        },
        {
          heading: "Changes to This Policy",
          body: [
            "We may update this policy from time to time. Material changes will be reflected by updating the effective date above.",
          ],
        },
      ]}
    />
  );
}

export function Disclosures() {
  return (
    <LegalPage
      title="Legal Disclosures"
      breadcrumbName="Legal Disclosures"
      path="/legal"
      effective="October 24, 2026"
      intro="Important notices about this website, the information it contains, and how information you send through it is handled by Pratt Law Group, PLLC d/b/a Lone Star Counsel."
      sections={[
        {
          heading: "Attorney Advertising",
          body: [
            "This website is attorney advertising under Part VII of the Texas Disciplinary Rules of Professional Conduct. The attorney responsible for its content is Darryl V. Pratt, Managing Partner, Pratt Law Group, PLLC d/b/a Lone Star Counsel, 2591 Dallas Parkway, Suite 300, Frisco, Texas 75034.",
            "Prior results do not guarantee a similar outcome. No representation is made that the quality of the legal services to be performed is greater than the quality of legal services performed by other lawyers.",
          ],
        },
        {
          heading: "Not Legal Advice",
          body: [
            "Articles, practice pages, FAQs, and other content on this website are general information about Texas law. They are not legal advice for any particular person, family, or business, and they may not reflect the most recent changes in the law.",
            "Do not act or refrain from acting on the basis of anything on this website without first consulting a lawyer about your specific facts.",
          ],
        },
        {
          heading: "No Attorney-Client Relationship",
          body: [
            "Reading this website, submitting a form, sending an email, or calling the firm does not create an attorney-client relationship. A relationship begins only when the firm has completed its conflicts check and both the client and the firm have signed a written engagement letter.",
          ],
        },
        {
          heading: "Privacy",
          body: [
            "We collect the information you choose to submit, such as your name, contact details, and a description of your matter, and use it only to respond to your inquiry and evaluate a possible engagement. We do not sell your information or share it for marketing. See our Privacy Policy at LoneStarCounsel.com/privacy for full details.",
          ],
        },
        {
          heading: "Website Form Confidentiality",
          body: [
            "Information sent through the website before an engagement is confirmed may not be protected by the attorney-client privilege, and the firm may not be able to keep it confidential if it later learns of a conflict of interest.",
            "Please do not send Social Security numbers, account numbers, medical records, or copies of sensitive documents through the website forms. Share only a brief description of your matter; an attorney will tell you how to send documents securely once the firm confirms it can represent you.",
          ],
        },
      ]}
    />
  );
}

export function Terms() {
  return (
    <LegalPage
      title="Terms of Use"
      breadcrumbName="Terms of Use"
      path="/terms"
      effective="February 1, 2026"
      intro="This website is provided by Pratt Law Group, PLLC d/b/a Lone Star Counsel for general information about the firm. Your use of the site is governed by these Terms."
      sections={[
        {
          heading: "Attorney Advertising",
          body: [
            "The content of this website is attorney advertising within the meaning of the applicable rules of professional conduct of the State Bar of Texas — including Part VII of the Texas Disciplinary Rules of Professional Conduct — and any other jurisdictions in which we may be admitted.",
            "The attorney responsible for the content of this website is Darryl V. Pratt, Managing Partner of Pratt Law Group, PLLC d/b/a Lone Star Counsel. The firm’s principal office is 2591 Dallas Parkway, Suite 300, Frisco, Texas 75034. Pratt Law Group, PLLC d/b/a Lone Star Counsel is a Texas Professional Limited Liability Company organized under Chapter 304 of the Texas Business Organizations Code.",
            "Prior results do not guarantee, warrant, or predict a similar outcome in any future matter. No representation is made that the quality of the legal services to be performed is greater than the quality of legal services performed by other lawyers. Any testimonials or endorsements on this site have been provided with client consent and are anonymized; they do not constitute a guarantee, warranty, or prediction of the outcome of your legal matter.",
          ],
        },
        {
          heading: "No Attorney-Client Relationship",
          body: [
            "Reading this website, submitting a form, sending an email, or otherwise communicating with the firm does not create an attorney-client relationship. An attorney-client relationship with Pratt Law Group, PLLC d/b/a Lone Star Counsel arises only upon execution of a written engagement letter signed by both the client and the firm — and, where applicable, upon the firm’s completion of its conflicts-of-interest check.",
          ],
        },
        {
          heading: "Not Legal Advice",
          body: [
            "The information on this website is provided for general educational purposes only and is not legal advice. Legal matters are fact-specific and jurisdiction-specific; you should not act or refrain from acting on the basis of any information on this site without seeking counsel qualified in the relevant jurisdiction. Neither the firm nor any of its attorneys is your attorney unless and until a written engagement letter has been executed by both parties.",
          ],
        },
        {
          heading: "Confidentiality of Communications",
          body: [
            "Unsolicited information sent to the firm through this website is not protected by the attorney-client privilege until a formal engagement is confirmed in writing. Please do not send confidential or sensitive information through the website contact forms until we have confirmed the firm can represent you.",
            "For confidential communications with existing clients of the firm, please use the secure client portal at LoneStarCounsel.com/portal or contact your assigned attorney by direct telephone.",
          ],
        },
        {
          heading: "Electronic Communications & SMS",
          body: [
            "By providing an email address or telephone number through this website, you consent to receive electronic communications from Pratt Law Group, PLLC d/b/a Lone Star Counsel regarding your inquiry. We do not send marketing text messages. Message and data rates may apply to any transactional message you receive. You may opt out of non-transactional communications at any time by writing to info@LoneStarCounsel.com.",
          ],
        },
        {
          heading: "Intellectual Property",
          body: [
            "All content on this website — including the Lone Star Counsel wordmark, the LSC monogram, the phrase “The Business, Wealth & Legacy Lifecycle,” editorial content, photography, and layout — is the property of Pratt Law Group, PLLC d/b/a Lone Star Counsel or its licensors and is protected by U.S. and international intellectual-property laws. You may not reproduce, distribute, or create derivative works without written permission.",
          ],
        },
        {
          heading: "Third-Party Links",
          body: [
            "This website may contain links to third-party websites. We do not endorse and are not responsible for the content, privacy practices, or terms of use of any linked site.",
          ],
        },
        {
          heading: "Jurisdiction",
          body: [
            "This website and these Terms are governed by the laws of the State of Texas without regard to conflict-of-laws principles. Any dispute arising out of your use of the site shall be brought exclusively in the state or federal courts located in Collin County, Texas. The firm’s attorneys are licensed in the State of Texas; matters arising in other jurisdictions may be handled in association with local counsel of record admitted in that jurisdiction.",
          ],
        },
        {
          heading: "Changes to These Terms",
          body: [
            "We may revise these Terms at any time. Continued use of the site after any change constitutes acceptance of the revised Terms. The effective date above reflects the current version.",
          ],
        },
      ]}
    />
  );
}
